export const QTY_DECIMAL_PLACES = 3;

/**
 * Parse user-entered quantity string — preserves up to 3 decimal places without rounding.
 * e.g. "1.123" stays 1.123, "1.12" stays 1.12 (not coerced to 1.120).
 */
export function parseQtyInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.') return 0;
  if (!/^\d+(\.\d{0,3})?$/.test(trimmed)) {
    const n = Number(trimmed);
    return Number.isFinite(n) ? coerceQty3(n) : 0;
  }
  const [wholePart, fractionPart = ''] = trimmed.split('.');
  const whole = Number(wholePart) || 0;
  if (!fractionPart) return whole;
  const fracLen = Math.min(fractionPart.length, QTY_DECIMAL_PLACES);
  const frac = Number(fractionPart.slice(0, QTY_DECIMAL_PLACES));
  return whole + frac / 10 ** fracLen;
}

/**
 * Normalize API/calculated floats to at most 3 dp (float noise only).
 * Do not use on user string input — use parseQtyInput instead.
 */
export function coerceQty3(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  const factor = 10 ** QTY_DECIMAL_PLACES;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** @deprecated Prefer parseQtyInput for user input and coerceQty3 for API values. */
export const roundQty3 = coerceQty3;

export function formatQty3(value: number | null | undefined): string {
  return coerceQty3(value ?? 0).toFixed(QTY_DECIMAL_PLACES);
}

/** Allow empty input or up to 3 decimal places while typing. */
export function isPartialQtyInput(value: string): boolean {
  return value === '' || /^\d*\.?\d{0,3}$/.test(value);
}

/** Prefer pending text input over stored numeric state (e.g. before blur / on save). */
export function resolveQtyFromInput(
  pendingInput: string | undefined,
  storedQty: number,
  parseFn: (value: string) => number = parseQtyInput,
): number {
  if (pendingInput !== undefined && pendingInput.trim() !== '') {
    return parseFn(pendingInput);
  }
  return storedQty;
}

/** Raw-item category "Machinery" (case-insensitive). */
export function isMachineryCategory(categoryName?: string | null): boolean {
  return String(categoryName ?? '').trim().toLowerCase() === 'machinery';
}

export function getRawItemCategoryName(
  rawItem?: { categoryName?: string | null; itemCategory?: { categoryName?: string | null } | null } | null
): string {
  return String(rawItem?.itemCategory?.categoryName ?? rawItem?.categoryName ?? '').trim();
}

/**
 * Machinery unit prices can be crores; 3 dp qty cannot represent rupee amounts.
 * Use enough decimals so amount ≈ qty × rate within 2 dp money rounding.
 */
export const MACHINERY_QTY_DECIMAL_PLACES = 10;

export function coerceMachineryQty(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  const factor = 10 ** MACHINERY_QTY_DECIMAL_PLACES;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** Derive received qty from amount ÷ unit price (Machinery — high precision). */
export function qtyFromAmount(amount: number, unitPrice: number): number {
  const rate = Number(unitPrice) || 0;
  if (rate <= 0) return 0;
  return coerceMachineryQty((Number(amount) || 0) / rate);
}

export function formatMachineryQty(value: number | null | undefined): string {
  const n = coerceMachineryQty(value ?? 0);
  if (n === 0) return '';
  // Trim trailing zeros but keep at least 3 dp for consistency with other qty fields
  const fixed = n.toFixed(MACHINERY_QTY_DECIMAL_PLACES);
  return fixed.replace(/(\.\d{3,}?)0+$/, '$1').replace(/\.$/, '');
}

export function isPartialMachineryQtyInput(value: string): boolean {
  return value === '' || new RegExp(`^\\d*\\.?\\d{0,${MACHINERY_QTY_DECIMAL_PLACES}}$`).test(value);
}

export function parseMachineryQtyInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.') return 0;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return 0;
  return coerceMachineryQty(n);
}

/** Allow empty money input or up to 2 decimal places while typing. */
export function isPartialMoneyInput(value: string): boolean {
  return value === '' || /^\d*\.?\d{0,2}$/.test(value);
}

export function parseMoneyInput(value: string): number {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.') return 0;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n + Number.EPSILON) * 100) / 100;
}
